import { useState } from "react";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";

import { IPaginator } from "common/types";
import { Optional } from "common/utils/types";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";

import PillButton from "client/common/components/buttons/PillButton";

import { ProductSearchFilters, SelectedProductFilters } from "../types";
import { getSearchString } from "../utils";
import ProductSearchFilterModal from "./ProductSearchFilterModal";
import ProductSearchFilterTags from "./ProductSearchFilterTags";

import styles from "../styles/ProductCardListHeader.module.less";

type ProductCardListHeaderProps = {
  paginator: IPaginator;
  isRefetching: boolean;
  /** Pluralized in the count, e.g. "deal" reads "Showing 1 - 14 of 14 deals". */
  entityName: string;
  options: Optional<ProductSearchFilters>;
  filters: SelectedProductFilters;
  onChange: (search: Partial<SelectedProductFilters>) => void;
  showFilters?: boolean;
};

type FilterActionsProps = Pick<ProductCardListHeaderProps, "options" | "filters" | "onChange">;

const FilterActions = ({ options, filters, onChange }: FilterActionsProps) => {
  const [isFiltering, setIsFiltering] = useState(false);

  return (
    <div className={styles.actions}>
      {options && <ProductSearchFilterTags options={options} value={filters} onChange={onChange} />}
      <PillButton
        variant="secondary"
        size="s"
        type="button"
        onClick={() => setIsFiltering(true)}
        disabled={!options}
      >
        <span className={styles.filter}>
          <Icon icon={faFilter} /> Filter
        </span>
      </PillButton>
      {options && (
        <ProductSearchFilterModal
          options={options}
          isVisible={isFiltering}
          onClose={() => setIsFiltering(false)}
          value={filters}
          onChange={onChange}
        />
      )}
    </div>
  );
};

/** The bar above a ProductCardList: result count, active filter chips, and the filter modal. */
const ProductCardListHeader = ({
  paginator,
  isRefetching,
  entityName,
  options,
  filters,
  onChange,
  showFilters = true,
}: ProductCardListHeaderProps) => (
  <div className={styles.header}>
    <div className={styles.label}>
      {isRefetching ? <Spinner /> : getSearchString(paginator, entityName)}
    </div>
    {showFilters && <FilterActions options={options} filters={filters} onChange={onChange} />}
  </div>
);

export default ProductCardListHeader;
